<template>
  <div class="flex-1 flex flex-col overflow-hidden bg-white p-3">

    <div class="grid grid-cols-6 gap-2 py-2">
      <input
        v-model="search"
        @change="fetch"
        type="text"
        placeholder="Search by Name or BRN"
        class="col-span-2 w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" />
      <select v-model="status" @change="fetch"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary">
        <option :value="null">Filter by Status</option>
        <option value="Registered">Registered</option>
        <option value="Insolvent">Insolvent</option>
        <option value="Ceased">Ceased</option>
      </select>
    </div>

    <div class="bg-white shadow overflow-hidden flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 border-b border-gray-100 sticky top-0">
          <tr>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">#</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Number</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
          </tr>
          </thead>
          <tbody>
          <template v-if="!loading && entities.length > 0">
            <tr v-for="(entity, idx) in entities" :key="idx"
                class="border-b border-gray-100 hover:bg-gray-50 transition">
              <td class="py-3 px-4 font-bold">{{ (currentPage - 1) * limit + idx + 1 }}</td>
              <td class="py-3 px-4 font-bold text-primary">{{ entity.no }}</td>
              <td class="py-3 px-4 font-bold">{{ entity.name }}</td>
              <td class="py-3 px-4">{{ dateFormat(entity.createdAt) }}</td>
              <td class="py-3 px-4">{{ entity.status }}</td>
              <td class="py-3 px-4">
                <button @click="editEntity(entity)" class="btn-sm-primary" title="Edit">
                  <i class="fa fa-pencil"></i> Edit
                </button>
              </td>
            </tr>
          </template>
          <tr v-if="loading">
            <td colspan="6" class="py-16 text-center">
              <i class="fa fa-spinner fa-spin text-primary text-2xl"></i>
            </td>
          </tr>
          <template v-if="!loading && entities.length === 0">
            <tr>
              <td colspan="6" class="py-16 text-center">
                <div class="flex flex-col items-center justify-center text-gray-400">
                  <i class="fa fa-folder-open text-6xl mb-4"></i>
                  <p class="text-lg font-medium text-gray-500">No MDAs Found</p>
                  <p class="text-sm text-gray-400 mt-2">No MDA list entries in the registry.</p>
                </div>
              </td>
            </tr>
          </template>
          </tbody>
        </table>
      </div>
      <Pagination :pagination="pagination" :page="currentPage" :limit="limit" label="MDAs" @update="update" />
    </div>
  </div>

  <AppModal v-model="showEditName" size="lg" title="Edit Entity Details" hide-footer>
    <div class="p-4">
      <table class="w-full text-sm mb-4">
        <tbody>
        <tr class="border-b border-gray-100">
          <td class="py-2 font-semibold text-gray-600 w-1/3">Name</td>
          <td class="py-2">
            <input
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              v-model="entity.name"
              placeholder="Name" />
          </td>
        </tr>
        <tr class="border-b border-gray-100">
          <td class="py-2 font-semibold text-gray-600">Date Added</td>
          <td class="py-2">{{ entity.no }}</td>
        </tr>
        <tr class="border-b border-gray-100">
          <td class="py-2 font-semibold text-gray-600">Date Created</td>
          <td class="py-2">{{ entity.createdAt }}</td>
        </tr>
        <tr>
          <td class="py-2 font-semibold text-gray-600">Status</td>
          <td class="py-2">{{ entity.status }}</td>
        </tr>
        </tbody>
      </table>
      <div class="flex justify-end">
        <button class="btn-sm-primary" @click="updateEntity" type="button" :disabled="loading">
          <i v-if="loading" class="fa fa-spinner fa-spin mr-1"></i> Update
        </button>
      </div>
    </div>
  </AppModal>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import { dateFormat } from "@/commons/composables/utilities";
import { api } from "@/app/services/api.service";
import { useToast } from "@/composables";
import { useBusinessRegistryStore } from "@/stores/business-registry";
import Pagination from "@/commons/components/Pagination.vue";
import type { Pagination as PaginationType } from "@/app/types";

interface Entity {
  no: string | null;
  name: string;
  createdAt: string;
  updatedAt: string;
  status: string;
}

const registryStore = useBusinessRegistryStore();
const { successToast, errorToast } = useToast();

const showEditName = ref(false)
const loading = ref(false)
const currentPage = ref(1)
const limit = ref(15)
const entities = ref<Entity[]>([])
const pagination = ref<PaginationType | undefined>(undefined);
const search = ref("")
const status = ref<string | null>(null)
const entity = ref<Entity>({ no: null, name: "", createdAt: "", updatedAt: "", status: "" });

onMounted(() => {
  fetch();
})

const fetch = () => {
  loading.value = true;
  registryStore.fetchMDAListRegistry({
    search: search.value,
    page: currentPage.value,
    limit: limit.value,
    offset: limit.value
  });
}

const editEntity = (data: Entity) => {
  entity.value = data;
  showEditName.value = true;
}

const updateEntity = async () => {
  loading.value = true;
  await api()
    .post("/configuration/mda-registry/" + entity.value.no, { name: entity.value.name })
    .then(() => {
      successToast("Success", "Entity details updated");
      fetch();
      showEditName.value = false;
    })
    .catch(error => {
      errorToast("Error", error.response?.data?.message || "Failed to update entity details");
    })
    .finally(() => {
      loading.value = false;
    });
}

function update(pg: number) {
  currentPage.value = pg;
  fetch();
}

watch(() => registryStore.mda_list_registry, (data) => {
  if (data) {
    loading.value = false;
    entities.value = data.content || [];
    pagination.value = {
      currentPage: currentPage.value,
      nextPage: currentPage.value + 1,
      totalRows: data.totalElements || 0,
      totalPages: data.totalPages || 0
    };
  }
})

watch(() => registryStore.error, (data) => {
  if (data) loading.value = false;
});
</script>
